/**
 * IndexedDB-backed OCR result cache.
 *
 * Ported from the original SpatchAI intelligent-caching system. The DB name,
 * version, object stores and key paths are kept identical so an existing cache
 * created by the legacy build continues to resolve. The image hash is a weak,
 * sampled fingerprint (every 256th byte) — it is NOT cryptographic; it exists
 * only to key OCR results and its algorithm is preserved byte-for-byte.
 */

const OCR_CACHE_DB = 'spatchai_ocr_cache';
const OCR_CACHE_VER = 1;

let ocrDb: IDBDatabase | null = null;

// Open (and lazily cache) the IndexedDB handle used for OCR image caching.
function ocrOpenDb(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>(function (res, rej) {
    if (ocrDb) return res(ocrDb);
    const req = indexedDB.open(OCR_CACHE_DB, OCR_CACHE_VER);
    req.onupgradeneeded = function (e) {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('images')) db.createObjectStore('images', { keyPath: 'id', autoIncrement: true });
      if (!db.objectStoreNames.contains('results')) db.createObjectStore('results', { keyPath: 'hash' });
    };
    req.onsuccess = function (e) { ocrDb = (e.target as IDBOpenDBRequest).result; res(ocrDb); };
    req.onerror = function () { rej(req.error); };
  });
}

/**
 * Hash an image blob for cache lookup (simple sampled blob-based hash).
 * Samples every 256th byte and hex-joins them, prefixed and truncated exactly
 * as the original implementation did.
 */
export async function hashImage(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const arr = new Uint8Array(buf);
  let hash = '';
  for (let i = 0; i < arr.length; i += 256) hash += arr[i].toString(16);
  return 'ocr_' + hash.slice(0, 32);
}

/** Get a cached OCR result by image hash, or null if absent. */
export function ocrCacheGet(hash: string): Promise<string | null> {
  return ocrOpenDb().then(function (db) {
    return new Promise<string | null>(function (res) {
      const tx = db.transaction('results', 'readonly').objectStore('results').get(hash);
      tx.onsuccess = function () { res(tx.result ? (tx.result as { text: string }).text : null); };
      tx.onerror = function () { res(null); };
    });
  }).catch(function () { return null; });
}

/** Store an OCR result in the cache, keyed by image hash. */
export function ocrCachePut(hash: string, text: string): Promise<void> {
  return ocrOpenDb().then(function (db) {
    return new Promise<void>(function (res) {
      const tx = db.transaction('results', 'readwrite').objectStore('results')
        .put({ hash: hash, text: text, timestamp: Date.now() });
      tx.onsuccess = function () { res(); };
      tx.onerror = function () { res(); };
    });
  }).catch(function () { return; });
}

/** Delete ONE cached OCR result by image hash (used by per-juror deletion). */
export function ocrCacheDelete(hash: string): Promise<void> {
  return ocrOpenDb().then(function (db) {
    return new Promise<void>(function (res) {
      const tx = db.transaction('results', 'readwrite').objectStore('results').delete(hash);
      tx.onsuccess = function () { res(); };
      tx.onerror = function () { res(); };
    });
  }).catch(function () { return; });
}

/**
 * Purge ALL cached OCR results and images from IndexedDB. Used by the panel's
 * "Delete All Juror Information" wipe so no recognized text lingers on-device
 * after jury selection is complete. Resolves even if the store is unavailable.
 */
export function ocrCacheClear(): Promise<void> {
  return ocrOpenDb().then(function (db) {
    return new Promise<void>(function (res) {
      const tx = db.transaction(['results', 'images'], 'readwrite');
      tx.objectStore('results').clear();
      tx.objectStore('images').clear();
      tx.oncomplete = function () { res(); };
      tx.onerror = function () { res(); };
    });
  }).catch(function () { return; });
}
